/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Card, DonutChart, Text } from '@tremor/react'
import { Select, MenuItem, FormControl, CircularProgress, IconButton } from '@material-ui/core'
import { KeyboardArrowDownRounded } from '@material-ui/icons'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import CustomTooltip from 'features/Main/NewDashboard/components/CustomTooltip'
import { formatDonutChartData } from 'utils/chart'

import { useLoadingTracker } from '../../../hooks/useLoadingTracker'
import {
  getFoodPreferenceAnalysisByLevel,
  FoodPreferenceAnalysisByLevelData,
  getUserPollQuestions,
  UserPollQuestion,
  UserPollQuestionsResponse,
} from './api'

interface FoodPreferenceDonutChartProps {
  period: { month: number; year: number }
  storeId?: number
  trigger: number
  compara?: boolean
  compareStoreId?: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const foodColors = ['teal', 'sky', 'green', 'emerald', 'lime']
const emptyDataColor = ['gray']
const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const arrowUp = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 10V2M6 2L2 6M6 2L10 6"
      stroke="#3A5A40"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const arrowDown = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 2V10M6 10L2 6M6 10L10 6"
      stroke="#B91C1C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const FoodPreferenceDonutChart: React.FC<FoodPreferenceDonutChartProps> = ({
  period,
  storeId,
  trigger,
  compara = false,
  compareStoreId,
  onLoaded,
}) => {
  const [data, setData] = useState<FoodPreferenceAnalysisByLevelData | null>(null)
  const [compareData, setCompareData] = useState<FoodPreferenceAnalysisByLevelData | null>(null)
  const [questions, setQuestions] = useState<UserPollQuestion[]>([])
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null)
  const canFetchRef = useRef(false)
  const skipQuestionFetchRef = useRef(true)
  const pendingInitialLoadRef = useRef(false)
  const [statsCall, statsLoading] = useApiCall<
    { startDate: string; endDate: string; storeId?: number; questionId?: number },
    FoodPreferenceAnalysisByLevelData
  >(getFoodPreferenceAnalysisByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'FoodPreferenceDonutChart' })
  const [questionsCall, questionsLoading] = useApiCall<
    Record<string, never>,
    UserPollQuestionsResponse
  >(getUserPollQuestions)

  const handleQuestionChange = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    const questionId = parseInt(e.target.value as string, 10)
    setSelectedQuestionId(questionId)
  }, [])

  const fetchData = useCallback(
    async (questionId: number, callOnLoaded = false) => {
      const startDate = moment()
        .year(period.year)
        .month(period.month)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')
      try {
        const response = await statsCall({
          startDate,
          endDate,
          storeId,
          questionId,
        })
        setData(response)
        if (compara && compareStoreId) {
          const compareResponse = await statsCall({
            startDate,
            endDate,
            storeId: compareStoreId,
            questionId,
          })
          setCompareData(compareResponse)
        } else {
          setCompareData(null)
        }
      } finally {
        if (callOnLoaded && onLoaded) {
          onLoaded()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, storeId, compara, compareStoreId, onLoaded],
  )

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await questionsCall({})
        setQuestions(response.userPolls)
        // Seleccionar la pregunta con defaultDashboard = true por defecto, o la primera activa si no hay ninguna marcada como default
        if (!selectedQuestionId) {
          const defaultQuestion = response.userPolls.find(q => q.active && q.defaultDashboard)
          if (defaultQuestion) {
            setSelectedQuestionId(defaultQuestion.id)
          } else {
            const firstActiveQuestion = response.userPolls.find(q => q.active)
            if (firstActiveQuestion) {
              setSelectedQuestionId(firstActiveQuestion.id)
            }
          }
        }
      } catch (error) {
        console.error('Error cargando preguntas:', error)
      }
    }
    fetchQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      if (selectedQuestionId) {
        fetchData(selectedQuestionId, true)
      } else {
        pendingInitialLoadRef.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, compara, compareStoreId])

  useEffect(() => {
    if (skipQuestionFetchRef.current) {
      skipQuestionFetchRef.current = false
      return
    }
    if (!canFetchRef.current || !selectedQuestionId) return
    const callOnLoaded = pendingInitialLoadRef.current
    pendingInitialLoadRef.current = false
    fetchData(selectedQuestionId, callOnLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionId])

  if (questionsLoading) {
    return <div style={montserratStyle}>Cargando preguntas...</div>
  }

  const compareMap = compareData ? Object.fromEntries(compareData.data.map(i => [i.tagId, i])) : {}

  // Ordenar segmentos de mayor a menor según cantidad
  const orderedData = data ? [...data.data].sort((a, b) => b.value - a.value) : []

  const isEmptyData =
    orderedData.length === 1 && orderedData[0].value === 0 && orderedData[0].tagId === 'empty'
  const colorsToUse = isEmptyData ? emptyDataColor : foodColors

  // Crear array de colores con la misma longitud que orderedData, repitiendo los colores consecutivamente
  const extendedFoodColors = Array.from(
    { length: orderedData.length },
    (_, idx) => colorsToUse[idx % colorsToUse.length],
  )
  const donutData = formatDonutChartData(
    orderedData.map((item, idx) => ({
      name: item.name,
      value: item.value,
      color: colorsToUse[idx % colorsToUse.length],
    })),
  )
  const legendRows = orderedData.map((item, idx) => {
    const compareItem = compareMap[item.tagId]
    let diff = 0
    let isPositive = true
    let diffLabel = null
    if (compareItem) {
      diff = (item.averageFrequency ?? 0) - (compareItem.averageFrequency ?? 0)
      isPositive = diff >= 0
      diffLabel = (diff > 0 ? '+' : '') + diff.toFixed(2)
    }
    return {
      name: item.name,
      color: extendedFoodColors[idx],
      value: item.proportion,
      frequency: item.averageFrequency,
      diff: diffLabel,
      isPositive,
      showComparison: !!compareItem,
    }
  })

  const downloadFoodPreferenceCSV = (orderedDataCSV: typeof orderedData) => {
    const rows = [
      ['Categoría', 'Proporción (%)', 'Frecuencia promedio'],
      ...orderedDataCSV.map(row => [
        row.name,
        row.proportion,
        row.averageFrequency?.toFixed(2) ?? '-',
      ]),
    ]
    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'preferencia_comida.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="p-6 bg-white" style={{ ...montserratStyle, minWidth: 380 }}>
      <div className="flex flex-row justify-between items-center mb-4">
        <div className="text-lg font-bold" style={montserratStyle}>
          Preferencias
        </div>
        <IconButton
          disabled={statsLoading || !data}
          className="ml-2"
          aria-label="download"
          onClick={() => downloadFoodPreferenceCSV(orderedData)}
        >
          <GetAppIcon />
        </IconButton>
      </div>

      {questions.length > 0 && (
        <div className="mb-4">
          <FormControl fullWidth>
            <Select
              value={selectedQuestionId || ''}
              onChange={handleQuestionChange}
              style={{ ...montserratStyle }}
              IconComponent={KeyboardArrowDownRounded}
              disabled={questionsLoading}
            >
              {questionsLoading ? (
                <MenuItem value="">Cargando preguntas...</MenuItem>
              ) : (
                questions
                  .filter(q => q.active)
                  .map(question => (
                    <MenuItem key={question.id} value={question.id}>
                      {question.question}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
        </div>
      )}
      {statsLoading || !data ? (
        <div className="flex items-center justify-center h-60">
          {statsLoading ? (
            <CircularProgress size={40} />
          ) : (
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="w-60 h-60">
            <DonutChart
              data={donutData}
              category="value"
              index="name"
              colors={donutData.map(d => d.color)}
              showLabel={false}
              noDataText="No hay datos disponibles para mostrar en este momento."
              customTooltip={CustomTooltip}
            />
          </div>
          {!isEmptyData && (
            <div className="w-full mt-8">
              <div className="grid grid-cols-12 text-sm font-semibold mb-2" style={montserratStyle}>
                <div className="col-span-5">Categorías</div>
                <div className="col-span-3 text-center">Cantidad</div>
                <div className="col-span-4 text-center">Frecuencia</div>
              </div>
              {legendRows.map(row => (
                <div
                  key={row.name}
                  className="grid grid-cols-12 items-center py-2"
                  style={montserratStyle}
                >
                  <div className="col-span-5 flex items-center gap-2">
                    <span
                      className={`bg-${row.color}-500`}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        display: 'inline-block',
                      }}
                    />
                    <span>{row.name}</span>
                  </div>
                  <div className="col-span-3 text-center font-bold">{row.value} %</div>
                  <div className="col-span-4 text-center flex items-center justify-center gap-2">
                    {row.frequency?.toFixed(2) ?? '-'}
                    {row.showComparison && row.diff && (
                      <span
                        className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                          row.isPositive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                        style={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        {row.isPositive ? arrowUp : arrowDown}
                        <span>{row.diff}</span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

FoodPreferenceDonutChart.defaultProps = {
  storeId: undefined,
  compara: false,
  compareStoreId: undefined,
}

export default FoodPreferenceDonutChart
